import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useState } from 'react'
import { useSession } from '@/auth/useSession'
import { initialsFromDisplayName } from '@/lib/displayName'

/**
 * Account menu — an avatar in the page chrome, identity behind a click.
 *
 * The identity is not part of the task on this screen (pick a workspace), so
 * it sits in the brand row rather than the content column, folded down to
 * initials. Opening it is where the full picture lives: display name, the
 * ground-truth email under it (the name may be derived from the address, so
 * a user whose name was guessed wrong can still tell which account this is),
 * and the one action the menu can perform.
 *
 * Radix DropdownMenu handles aria-expanded, Escape, outside-click, focus
 * return and roving focus.
 */
export function UserBadge() {
  const { email, displayName, signout } = useSession()
  const [signingOut, setSigningOut] = useState(false)

  const primaryLabel = displayName ?? email ?? 'Account'
  const initials = initialsFromDisplayName(displayName ?? email)

  async function handleSignout() {
    setSigningOut(true)
    try {
      await signout()
    } catch {
      setSigningOut(false)
    }
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="idbtn"
          aria-label={email ? `Account menu — signed in as ${email}` : 'Account menu'}
        >
          <span className="idbtn__avatar" aria-hidden="true">
            {initials}
          </span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className="idmenu" sideOffset={8} align="end">
          <DropdownMenu.Label className="idmenu__head">
            <span className="idmenu__name">{primaryLabel}</span>
            {email && email !== primaryLabel ? (
              <span className="idmenu__email">{email}</span>
            ) : null}
          </DropdownMenu.Label>

          <DropdownMenu.Item
            className="idmenu__action idmenu__action--danger"
            disabled={signingOut}
            onSelect={(e) => {
              // Keep the menu mounted while the redirect is being set up —
              // closing it first would flash the bare page mid-sign-out.
              e.preventDefault()
              void handleSignout()
            }}
          >
            {signingOut ? 'Signing out…' : 'Sign out of Spaceti'}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
